import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, FileText } from 'lucide-react';

const Cookies = () => {
    const lastUpdated = 'January 15, 2025';

    return (
        <div className="min-h-screen bg-white">
            <Header />

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-gradient-to-br from-teal-50 to-green-50">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <Shield className="w-16 h-16 text-teal-600 mx-auto mb-6" />
                        <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                            Legal & Privacy
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            Your privacy and trust are important to us
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto">
                        {/* Cookies Policy */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="mb-16"
                        >
                            <div className="flex items-center mb-6">
                                <FileText className="w-8 h-8 text-teal-600 mr-3" />
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                    Cookies Policy
                                </h2>
                            </div>
                            <p className="text-sm text-gray-500 mb-8">Last updated: {lastUpdated}</p>

                            <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Cookies Policy</h3>
                                    <p className="leading-relaxed mb-4">
                                        At Maidly, we are committed to providing you with a smooth, personalized, and secure experience when using our website and mobile app. To achieve this, we use cookies and similar technologies to collect information about how you interact with our platform.
                                    </p>
                                    <p className="leading-relaxed mb-2">
                                        Cookies are small text files stored on your device that help us:
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li>Remember your preferences and settings</li>
                                        <li>Improve the functionality and performance of our website and app</li>
                                        <li>Analyze user behavior to enhance your experience</li>
                                        <li>Show relevant offers or recommendations</li>
                                    </ul>
                                    <p className="leading-relaxed mb-4">
                                        By using Maidly’s website or app, you consent to the use of cookies as described in this policy. You can manage or disable cookies at any time through your browser or device settings.
                                    </p>
                                    <p className="leading-relaxed">
                                        Our goal is to make your experience with Maidly as seamless and enjoyable as possible while being transparent about the information we collect.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Types of Cookies</h3>
                                    <p className="leading-relaxed mb-4">
                                        At Maidly, we use different types of cookies to enhance your experience and ensure our website and app work properly. These cookies fall into the following categories:
                                    </p>

                                    <div className="mb-6">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">1. Essential / Strictly Necessary Cookies</h4>
                                        <p className="leading-relaxed mb-2">These cookies are required for the website or app to function.</p>
                                        <ul className="list-disc pl-6 space-y-2 mb-2">
                                            <li>Without them, core services like booking a maid, logging in, or processing payments would not work.</li>
                                            <li>Example: Remembering your session while navigating different pages.</li>
                                        </ul>
                                    </div>

                                    <div className="mb-6">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">2. Performance / Analytics Cookies</h4>
                                        <p className="leading-relaxed mb-2">These cookies help us understand how visitors interact with our website and app.</p>
                                        <ul className="list-disc pl-6 space-y-2 mb-2">
                                            <li>They collect anonymous information such as the number of visitors, pages visited, and how users navigate the site.</li>
                                            <li><strong>Purpose:</strong> Improve website performance, fix errors, and enhance the user experience.</li>
                                            <li><strong>Example:</strong> Google Analytics or similar analytics tools.</li>
                                        </ul>
                                    </div>

                                    <div className="mb-6">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">3. Functional / Preference Cookies</h4>
                                        <p className="leading-relaxed mb-2">These cookies remember choices you make on the website to provide a personalized experience.</p>
                                        <ul className="list-disc pl-6 space-y-2 mb-2">
                                            <li><strong>Purpose:</strong> Remember your preferred language, location, or app settings.</li>
                                            <li><strong>Example:</strong> Saving your preferred service type so you don’t have to select it again.</li>
                                        </ul>
                                    </div>

                                    <div className="mb-6">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">4. Advertising / Targeting Cookies</h4>
                                        <p className="leading-relaxed mb-2">These cookies are used to deliver relevant advertisements and marketing messages.</p>
                                        <ul className="list-disc pl-6 space-y-2 mb-2">
                                            <li>They track browsing behavior across websites to show personalized offers or promotions.</li>
                                            <li><strong>Example:</strong> Displaying special Maidly promotions or ads for services you’ve shown interest in.</li>
                                        </ul>
                                    </div>

                                    <div className="mb-6">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">5. Third-Party Cookies</h4>
                                        <p className="leading-relaxed mb-2">Some cookies are set by third-party services we use, such as analytics tools, payment gateways, or social media platforms.</p>
                                        <ul className="list-disc pl-6 space-y-2 mb-2">
                                            <li><strong>Purpose:</strong> Help us provide additional features, analyze traffic, and run marketing campaigns.</li>
                                            <li><strong>Example:</strong> Payment processor remembering a completed transaction.</li>
                                        </ul>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">How Users Can Control Cookies</h3>
                                    <p className="leading-relaxed mb-4">
                                        At Maidly, we respect your privacy and give you control over how cookies are used while you browse our website or use our app. You can accept, block, or delete cookies based on your preferences. Here’s how:
                                    </p>

                                    <div className="mb-4">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">1. Browser Settings</h4>
                                        <p className="leading-relaxed mb-2">Most web browsers allow you to manage or disable cookies. You can block all cookies, delete existing cookies, or receive a warning before a cookie is stored.</p>
                                        <p className="leading-relaxed mb-2">Popular browsers have these settings in their “Privacy” or “Security” sections:</p>
                                        <ul className="list-disc pl-6 space-y-1 mb-2">
                                            <li><strong>Google Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                                            <li><strong>Mozilla Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                                            <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                                            <li><strong>Microsoft Edge:</strong> Settings → Cookies and site permissions</li>
                                        </ul>
                                    </div>

                                    <div className="mb-4">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">2. Device Settings</h4>
                                        <p className="leading-relaxed mb-2">If you are using a mobile device, cookies can also be managed through your device or app settings.</p>
                                        <p className="leading-relaxed">For Maidly app users, some cookies (like analytics or push notifications) can be controlled within the app’s settings.</p>
                                    </div>

                                    <div className="mb-4">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">3. Opt-Out Tools</h4>
                                        <p className="leading-relaxed mb-2">Some third-party cookies, like advertising and tracking cookies, may be managed using industry-standard opt-out tools.</p>
                                        <p className="leading-relaxed"><strong>Example:</strong> You can visit Network Advertising Initiative to manage targeted ads.</p>
                                    </div>

                                    <div className="mb-4">
                                        <h4 className="text-xl font-semibold text-gray-800 mb-2">4. Note</h4>
                                        <p className="leading-relaxed mb-2">Disabling certain cookies may affect the functionality of our website or app.</p>
                                        <p className="leading-relaxed">Some features, like making a booking, saving preferences, or accessing personalized offers, may not work properly if essential cookies are blocked.</p>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Third-Party Cookies</h3>
                                    <p className="leading-relaxed mb-4">
                                        In addition to our own cookies, Maidly may use third-party cookies to enhance the functionality of our website and app, provide analytics, and deliver personalized services.
                                    </p>
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">What Are Third-Party Cookies?</h4>
                                    <p className="leading-relaxed mb-4">
                                        Third-party cookies are set by external service providers, not by Maidly directly. These cookies allow the third parties to collect information about your interactions with our site or app to provide additional features or services.
                                    </p>
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Why We Use Third-Party Cookies</h4>
                                    <p className="leading-relaxed mb-2">We use third-party cookies for the following purposes:</p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li><strong>Analytics & Performance:</strong> Tools like Google Analytics help us understand user behavior, track website performance, and improve our services.</li>
                                        <li><strong>Payment & Security Services:</strong> Payment gateways or security providers may set cookies to process transactions safely and securely.</li>
                                        <li><strong>Advertising & Marketing:</strong> Third-party advertising networks may use cookies to display relevant promotions or offers based on your interests.</li>
                                        <li><strong>Social Media Integration:</strong> Services like Facebook or Instagram may set cookies to enable sharing content or logging in via social media.</li>
                                    </ul>
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Your Control Over Third-Party Cookies</h4>
                                    <p className="leading-relaxed mb-2">
                                        You can disable or block third-party cookies through your browser or device settings.
                                    </p>
                                    <p className="leading-relaxed mb-4">
                                        Many third-party providers also offer opt-out options for targeted advertising. For example: Network Advertising Initiative
                                    </p>
                                    <p className="leading-relaxed">
                                        <strong>Note:</strong> Blocking third-party cookies may limit some features of the website or app, such as personalized recommendations, analytics tracking, or secure payment processing.
                                    </p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Policy Updates</h3>
                                    <p className="leading-relaxed mb-4">
                                        At Maidly, we are committed to keeping our users informed about how we collect and use cookies. To ensure transparency, this Cookies Policy may be updated from time to time.
                                    </p>
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">How Updates Are Handled</h4>
                                    <p className="leading-relaxed mb-2">Any changes to this Cookies Policy will be published on this page with the updated date clearly mentioned. Updates may include:</p>
                                    <ul className="list-disc pl-6 space-y-2 mb-4">
                                        <li>New types of cookies we start using</li>
                                        <li>Changes to how existing cookies are used</li>
                                        <li>Changes to third-party services or providers</li>
                                        <li>Updates due to legal or regulatory requirements</li>
                                    </ul>
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">User Responsibility</h4>
                                    <p className="leading-relaxed mb-2">We encourage all users to review this page periodically to stay informed about the cookies we use.</p>
                                    <p className="leading-relaxed">Continued use of the Maidly website or app after updates are posted means that you accept and agree to the revised policy.</p>
                                </div>

                                <div>
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">Contact for Clarifications</h3>
                                    <p className="leading-relaxed mb-4">
                                        If you have any questions, concerns, or require further clarification about the Cookies Policy, you can contact Maidly Customer Support via our website, app, or email.
                                    </p>
                                    <p className="leading-relaxed">
                                        We are committed to responding promptly and ensuring you understand how your information is used.
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        {/* Contact Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="mt-16 bg-gradient-to-br from-teal-50 to-green-50 rounded-3xl p-8 text-center"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                Have Questions?
                            </h3>
                            <p className="text-gray-600 mb-6">
                                If you have any questions about our policies, please contact us
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <a href="mailto:legal@maidly.in" className="text-teal-600 font-semibold hover:underline">
                                    legal@maidly.in
                                </a>
                                <span className="hidden sm:inline text-gray-400">|</span>
                                <a href="tel:18001234567" className="text-teal-600 font-semibold hover:underline">
                                    9257652266
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Cookies;
